import PageHeader from "@/components/site/PageHeader";
import { ORACLE_MODULES, IMAGES } from "@/constants/siteData";
import { CheckCircle2, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { COMPANY } from "@/constants/siteData";

export default function OraclePage() {
  return (
    <div data-testid="oracle-page">
      {/* Dark hero to signify deep tech */}
      <header className="relative overflow-hidden bg-ink-deep text-white pt-32 md:pt-40 pb-24 md:pb-32">
        <div
          aria-hidden
          className="absolute inset-0 opacity-30"
          style={{ backgroundImage: `url(${IMAGES.tech})`, backgroundSize: "cover", backgroundPosition: "center" }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B1220] via-[#0B1220]/70 to-transparent" />
        <div aria-hidden className="pointer-events-none absolute inset-0">
          <div className="absolute top-24 -left-20 h-[420px] w-[420px] rounded-full bg-[#1E5AF6]/30 blur-[110px] aurora" />
          <div className="absolute -bottom-32 right-0 h-[460px] w-[460px] rounded-full bg-[#16A374]/25 blur-[110px] aurora" />
        </div>
        <div className="container-page relative">
          <p className="eyebrow text-white/60 mb-6">O · Oracle Fusion Cloud Partner</p>
          <h1
            data-testid="oracle-hero-title"
            className="font-display font-extrabold text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.9] tracking-tighter max-w-5xl"
          >
            We are Oracle partners
            <br />
            for <span className="grad-text">Fusion Cloud.</span>
          </h1>
          <p className="mt-10 max-w-2xl text-lg text-white/70 leading-relaxed">
            End-to-end Oracle capability — implementation, integration, extension,
            managed services, and migration across Fusion Apps, EPM, OIC, OCI,
            and E-Business Suite. Now with AI copilots and analytics woven
            through every module.
          </p>
        </div>
      </header>

      <section className="py-24 md:py-32">
        <div className="container-page">
          {/* Authority band — AI-first Oracle capabilities */}
          <div className="mb-12 md:mb-16 grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
            <div className="md:col-span-8">
              <p className="eyebrow mb-4">
                <span className="inline-block h-2 w-2 mr-1 align-middle bg-gradient-to-br from-[#1E5AF6] to-[#16A374]" />{" "}
                Expertise &amp; authority · AI on Oracle
              </p>
              <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tighter leading-[1.05]">
                We are experts and an authority on{" "}
                <span className="grad-text">
                  Oracle AI, GenAI, Fusion Agentic Apps and the AI Data Platform.
                </span>
              </h2>
              <p className="mt-6 max-w-2xl text-[#4B5563] text-lg leading-relaxed">
                From OCI Generative AI and 23ai Vector Search to Fusion AI
                Agents and enterprise-grade MLOps — we design, build and run
                production-grade AI on Oracle, layered across every Fusion
                pillar and E-Business Suite.
              </p>
            </div>
            <div className="md:col-span-4 grid grid-cols-2 gap-3">
              {[
                { k: "AI", v: "Oracle AI Services" },
                { k: "GenAI", v: "OCI Generative AI" },
                { k: "Agents", v: "Fusion Agentic Apps" },
                { k: "Data", v: "23ai + Vector" },
              ].map((p) => (
                <div
                  key={p.k}
                  className="border border-[#e5e7eb] bg-white p-4 shine box-elevated"
                >
                  <p className="font-mono text-[10px] tracking-[0.24em] uppercase text-[#16A374]">
                    {p.k}
                  </p>
                  <p className="font-display font-bold text-sm mt-1 leading-snug">
                    {p.v}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 border-t border-l border-[#e5e7eb] box-elevated bg-white">
            {ORACLE_MODULES.map((m) => (
              <article
                key={m.key}
                data-testid={`oracle-module-${m.key}`}
                className={`relative shine border-r border-b border-[#e5e7eb] p-8 md:p-10 group transition-colors ${
                  m.featured
                    ? "hover:bg-gradient-to-br hover:from-[#0B1220] hover:to-[#0E1E5A] hover:text-white"
                    : "hover:bg-[#f9fafb]"
                }`}
              >
                {m.featured && (
                  <span className="absolute top-0 left-0 h-[3px] w-full bg-gradient-to-r from-[#1E5AF6] via-[#23D89A] to-[#16A374]" />
                )}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <p className="font-mono text-xs tracking-[0.2em] uppercase text-[#4B5563] group-hover:text-white/60">
                      {m.number}
                    </p>
                    {m.featured && (
                      <span
                        data-testid={`oracle-module-${m.key}-badge`}
                        className="inline-flex items-center gap-1.5 border border-[#16A374]/40 text-[#16A374] group-hover:text-[#23D89A] group-hover:border-[#23D89A]/50 px-2 py-0.5 font-mono text-[10px] tracking-[0.2em] uppercase"
                      >
                        <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#16A374] group-hover:bg-[#23D89A]" />
                        AI · Authority
                      </span>
                    )}
                  </div>
                  <span className="h-1 w-8 bg-gradient-to-r from-[#1E5AF6] to-[#16A374]" />
                </div>
                <h2 className="font-display text-2xl md:text-3xl font-bold tracking-tight leading-tight mb-6">
                  {m.title}
                </h2>
                <ul className="space-y-3">
                  {m.items.map((it, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-3 text-[#0B1220] group-hover:text-white"
                    >
                      <CheckCircle2 className="w-4 h-4 mt-1 text-[#16A374] group-hover:text-[#23D89A] shrink-0" />
                      <span className="text-sm leading-relaxed">{it}</span>
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-ink-deep text-white py-24 md:py-32">
        <div aria-hidden className="pointer-events-none absolute inset-0">
          <div className="absolute -top-20 right-0 h-[420px] w-[420px] rounded-full bg-[#1E5AF6]/25 blur-[100px] aurora" />
          <div className="absolute bottom-0 -left-24 h-[420px] w-[420px] rounded-full bg-[#16A374]/25 blur-[100px] aurora" />
        </div>
        <div className="container-page grid md:grid-cols-12 gap-10 items-center relative">
          <div className="md:col-span-8">
            <p className="eyebrow text-white/70 mb-4">Engagement</p>
            <h2 className="font-display text-4xl md:text-5xl font-extrabold tracking-tighter">
              Ready to modernise on Oracle Fusion?
            </h2>
            <p className="mt-6 max-w-2xl text-white/70 text-lg leading-relaxed">
              From discovery and product fitment through implementation, cutover
              and post-go-live support — we deliver measurable outcomes on Oracle.
            </p>
          </div>
          <div className="md:col-span-4 md:text-right">
            <a
              data-testid="oracle-cta-contact"
              href={`mailto:${COMPANY.email}?subject=Oracle%20Fusion%20discovery`}
              className="group relative shine shine-dark box-elevated-dark inline-flex items-center gap-2 bg-white text-[#0B1220] px-7 py-4 text-sm font-semibold hover:bg-[#1E5AF6] hover:text-white transition-colors"
            >
              Talk to our Oracle team
              <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </a>
            <div className="mt-6">
              <Link
                to="/contact"
                data-testid="oracle-cta-form"
                className="text-sm text-white/60 hover:text-white link-underline"
              >
                Or send us a message →
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
